import { BOARD_SIZE, isCore } from "./constants.js";
import type { Coord, Layout, Tile, TileKind } from "./types.js";

/** Row-major index for a coordinate. */
export function idx(x: number, y: number, size: number = BOARD_SIZE): number {
  return y * size + x;
}

export function coordOf(index: number, size: number = BOARD_SIZE): Coord {
  return { x: index % size, y: Math.trunc(index / size) };
}

export function inBounds(x: number, y: number, size: number = BOARD_SIZE): boolean {
  return x >= 0 && y >= 0 && x < size && y < size;
}

export function isEdge(x: number, y: number, size: number = BOARD_SIZE): boolean {
  return x === 0 || y === 0 || x === size - 1 || y === size - 1;
}

export function tileAt(layout: Layout, x: number, y: number): Tile | undefined {
  if (!inBounds(x, y, layout.size)) return undefined;
  return layout.tiles[idx(x, y, layout.size)];
}

/**
 * The three tile kinds that make up the path units walk.
 *
 * Mud and barricades are not obstacles beside the road — they ARE road tiles
 * with a different effect (02 GDD §6.2), so they count toward the road budget
 * and toward path continuity.
 */
export function isRoad(kind: TileKind | undefined): boolean {
  return kind === "road" || kind === "mud" || kind === "barricade";
}

/** The four orthogonal neighbours of a coordinate, in a fixed order (N, E, S, W). */
export function neighbours(x: number, y: number): readonly Coord[] {
  return [
    { x, y: y - 1 },
    { x: x + 1, y },
    { x, y: y + 1 },
    { x: x - 1, y },
  ];
}

/** Road neighbours of a tile, in the same fixed order. Deterministic. */
export function roadNeighbours(layout: Layout, x: number, y: number): readonly Coord[] {
  return neighbours(x, y).filter((n) => isRoad(tileAt(layout, n.x, n.y)?.kind));
}

/** True when the tile orthogonally touches one of the four core tiles. */
export function touchesCore(x: number, y: number): boolean {
  return neighbours(x, y).some((n) => isCore(n.x, n.y));
}

export type TraceResult =
  | { readonly ok: true; readonly path: readonly Coord[] }
  | { readonly ok: false; readonly code: string; readonly tiles: readonly Coord[] };

/**
 * Walk the road from the gate, one tile at a time, and return it in order.
 *
 * This is the single place the "one continuous, unbranched path" rule lives.
 * `validateLayout` uses it to reject illegal mazes; the movement code (S1-3)
 * uses the same ordered path to advance units, so the two can never disagree
 * about what the road is.
 *
 * The rule from 02 GDD §6.1: every road tile has exactly two road neighbours,
 * except the gate and the final tile, which have one each.
 */
export function tracePath(layout: Layout): TraceResult {
  const { x: gx, y: gy } = layout.gate;

  const start = tileAt(layout, gx, gy);
  if (!isRoad(start?.kind)) {
    return { ok: false, code: "GATE_NOT_ROAD", tiles: [layout.gate] };
  }

  const gateLinks = roadNeighbours(layout, gx, gy);
  if (gateLinks.length !== 1) {
    return {
      ok: false,
      code: gateLinks.length === 0 ? "GATE_ISOLATED" : "GATE_BRANCHES",
      tiles: [layout.gate, ...gateLinks],
    };
  }

  const path: Coord[] = [layout.gate];
  const seen = new Set<number>([idx(gx, gy, layout.size)]);
  let current = gateLinks[0]!;

  for (;;) {
    path.push(current);
    seen.add(idx(current.x, current.y, layout.size));

    const links = roadNeighbours(layout, current.x, current.y);
    if (links.length > 2) {
      return { ok: false, code: "ROAD_BRANCHES", tiles: [current, ...links] };
    }

    const next = links.filter((n) => !seen.has(idx(n.x, n.y, layout.size)));
    if (next.length === 0) break; // reached the end of the path

    // Exactly one unvisited neighbour: keep walking.
    current = next[0]!;
    if (path.length > layout.size * layout.size) {
      // Unreachable given `seen`, but a loop here would hang the server.
      return { ok: false, code: "ROAD_LOOPS", tiles: [current] };
    }
  }

  // Every road tile must belong to that one path — no islands elsewhere.
  const orphans: Coord[] = [];
  for (let i = 0; i < layout.tiles.length; i++) {
    if (!isRoad(layout.tiles[i]?.kind)) continue;
    if (seen.has(i)) continue;
    orphans.push(coordOf(i, layout.size));
  }
  if (orphans.length > 0) {
    return { ok: false, code: "ROAD_NOT_CONNECTED", tiles: orphans };
  }

  return { ok: true, path };
}

/** Which road tile a progress value sits on. */
export function tileIndexAt(progress: number, pathLength: number): number {
  const raw = Math.trunc(progress / 1000);
  return raw < 0 ? 0 : raw >= pathLength ? pathLength - 1 : raw;
}

/**
 * A unit's position on the board in milli-tiles, interpolated between the tile
 * it is leaving and the one it is entering.
 *
 * Towers need sub-tile precision: a unit halfway between two tiles is a
 * different distance from a tower than one standing on either. On the final
 * road tile there is no next tile, so it heads for the core it is about to reach.
 */
export function positionMilli(path: readonly Coord[], progress: number): Coord {
  const i = tileIndexAt(progress, path.length);
  const from = path[i];
  if (!from) return { x: 0, y: 0 };

  const frac = progress - i * 1000;
  const to = path[i + 1] ?? coreNeighbourOf(from);

  return {
    x: from.x * 1000 + (to.x - from.x) * frac,
    y: from.y * 1000 + (to.y - from.y) * frac,
  };
}

/** The core tile this road tile leads into. Deterministic: fixed neighbour order. */
function coreNeighbourOf(at: Coord): Coord {
  return neighbours(at.x, at.y).find((n) => isCore(n.x, n.y)) ?? at;
}

/**
 * A fresh board: the core in place, everything else empty, Wall and Guild 0.
 *
 * This is what the editor (S07) starts from. It is deliberately NOT a valid
 * layout — there is no road yet — so `validateLayout` rejects it until the
 * defender draws one.
 */
export function emptyLayout(size: number = BOARD_SIZE): Layout {
  const tiles: Tile[] = new Array(size * size);
  for (let i = 0; i < tiles.length; i++) {
    const at = coordOf(i, size);
    tiles[i] = { kind: isCore(at.x, at.y) ? "core" : "empty" };
  }
  return { size, tiles, gate: { x: 0, y: 0 }, wallLevel: 0, engineersGuildLevel: 0 };
}
